import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import type { ReactNode } from "react";

import { DataTable, FilterBar } from "@/components/data";
import type { FilterSpec } from "@/components/data";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { applyFilters, parseFilters, type FilterValues } from "@/lib/filters";

import {
  getCampaignLog,
  getCampaignLogDetail,
  getFormatOptions,
  messageFor,
  type CampaignLogDetail,
  type CampaignLogRow,
} from "./api";
import {
  campaignLogParams,
  campaignLogState,
  DEFAULT_DAYS,
  formatScore,
  parseDays,
  runLabel,
  verdictLabel,
  VERDICTS,
  WINDOWS,
} from "./format";

type Search = { values: FilterValues; set: (next: FilterValues) => void };

function useSearchValues(): Search {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  return {
    values: parseFilters(search),
    set: (next) => void navigate({ to: ".", search: applyFilters(search, next), replace: true }),
  };
}

const text = (values: FilterValues, key: string) =>
  typeof values[key] === "string" && values[key] ? (values[key] as string) : null;

function DetailLine({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex justify-between gap-4 border-b border-ks-grey-4 py-2 text-sm text-ks-ink">
      <span>{label}</span>
      <span className="text-right tabular-nums">{value}</span>
    </div>
  );
}

function AssetDetail({ asset }: { asset: CampaignLogDetail }) {
  const dim = (v: number | null) => (v === null ? "Not rated" : `${formatScore(v, 2)} / 2`);
  const pass = (v: number | null) => (v === null ? "Not scored" : formatScore(v, 0));
  return (
    <div className="flex flex-col gap-4 p-4">
      <section>
        <h3 className="pb-1 text-sm font-semibold text-ks-ink">Eval passes</h3>
        <DetailLine label="Snippet integrity" value={pass(asset.score_snippet_integrity)} />
        <DetailLine label="Communication" value={pass(asset.score_communication)} />
        <DetailLine label="KB consistency" value={pass(asset.score_kb_consistency)} />
      </section>
      <section>
        <h3 className="pb-1 text-sm font-semibold text-ks-ink">Asset dimensions</h3>
        <DetailLine label="Relate" value={dim(asset.relate)} />
        <DetailLine label="Champion Truth" value={dim(asset.champion_truth)} />
        <DetailLine label="Empower" value={dim(asset.empower)} />
        <DetailLine label="Brand Voice" value={dim(asset.brand_voice)} />
        <DetailLine label="Engagement" value={dim(asset.engagement)} />
        <DetailLine label="Marketing Engine Score" value={`${formatScore(asset.marketing_engine_score)} / 10`} />
      </section>
      <section>
        <h3 className="pb-1 text-sm font-semibold text-ks-ink">Provenance</h3>
        <DetailLine label="Run / campaign" value={runLabel(asset.marketing_run_id)} />
        <DetailLine label="Skeleton" value={asset.skeleton_slug} />
        <DetailLine
          label="Snippets used"
          value={asset.snippet_ids && asset.snippet_ids.length > 0 ? asset.snippet_ids.map((id) => `#${id}`).join(", ") : "None recorded"}
        />
        <DetailLine label="Core need" value={asset.core_need_id ?? "None recorded"} />
        <DetailLine label="Config version" value={`v${asset.config_version}`} />
        <DetailLine label="Rubric version" value={asset.asset_rubric_version ?? "Not recorded"} />
        <DetailLine label="Revisions" value={asset.revision_count ?? "Not recorded"} />
        <DetailLine label="Readability grade" value={formatScore(asset.readability_grade)} />
      </section>
    </div>
  );
}

function AssetSheet({ assetId, onClose, areaName }: { assetId: string | null; onClose: () => void; areaName: string }) {
  const detail = useQuery({
    queryKey: ["cvce_marketing", "campaign_log_detail", assetId],
    queryFn: () => getCampaignLogDetail(assetId as string),
    enabled: assetId !== null,
  });
  let body: ReactNode = <p className="p-4 text-sm text-ks-ink">Loading this asset…</p>;
  if (detail.isError) {
    body = (
      <div className="p-4">
        <Alert>
          <AlertTitle>Asset detail unavailable</AlertTitle>
          <AlertDescription>{messageFor(detail.error, areaName, "CVCE telemetry")}</AlertDescription>
        </Alert>
      </div>
    );
  } else if (detail.data && (!detail.data.data.available || !detail.data.data.asset)) {
    body = <p className="p-4 text-sm text-ks-ink">We can't reach CVCE telemetry right now. Try again shortly.</p>;
  } else if (detail.data?.data.asset) {
    body = <AssetDetail asset={detail.data.data.asset} />;
  }
  return (
    <Sheet open={assetId !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      {assetId !== null && (
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-[40vw]">
          <SheetHeader>
            <SheetTitle>Asset {assetId}</SheetTitle>
            <SheetDescription>Scores, snippets used and the config version it was drafted against.</SheetDescription>
          </SheetHeader>
          {body}
        </SheetContent>
      )}
    </Sheet>
  );
}

/**
 * Campaign Log (UX S3/W3): one row per marketing asset, newest first, grouped
 * by run where one is traced (G-3). Opening a row reads, it changes nothing,
 * so the open control is marked non-mutating for view-only people.
 */
export function CampaignLogTab({ areaName }: { areaName: string }) {
  const { values, set } = useSearchValues();
  const days = parseDays(values.days);
  const verdict = text(values, "verdict");
  const format = text(values, "format");
  const assetId = text(values, "asset");
  const q = campaignLogParams({ days, verdict, format });

  const formats = useQuery({ queryKey: ["cvce_marketing", "formats"], queryFn: getFormatOptions });
  const log = useQuery({ queryKey: ["cvce_marketing", "campaign_log", q], queryFn: () => getCampaignLog(q) });

  const formatValues = formats.data?.data.available
    ? [...new Set(formats.data.data.formats.map((f) => f.content_model).filter((m): m is string => Boolean(m)))].sort()
    : [];
  const filters: FilterSpec[] = [
    { name: "days", label: "Window", kind: "choice", options: WINDOWS.map((w) => ({ value: w, label: `Last ${w} days` })) },
    {
      name: "format",
      label: "Format",
      kind: "choice",
      anyLabel: "All",
      disabled: formats.isError || (formats.data !== undefined && !formats.data.data.available),
      options: formatValues.map((f) => ({ value: f, label: f })),
    },
    {
      name: "verdict",
      label: "Verdict",
      kind: "choice",
      anyLabel: "All",
      options: VERDICTS.map((v) => ({ value: v, label: verdictLabel(v) })),
    },
  ];

  const open = (row: CampaignLogRow) => set({ ...values, asset: row.asset_id });
  const columns: ColumnDef<CampaignLogRow, unknown>[] = [
    { id: "date", header: "Date", accessorFn: (row) => row.created_at.slice(0, 10) },
    { id: "run", header: "Run / campaign", accessorFn: (row) => runLabel(row.marketing_run_id) },
    { id: "format", header: "Format", accessorFn: (row) => row.content_model ?? "Not recorded" },
    { id: "skeleton", header: "Skeleton", accessorFn: (row) => row.skeleton_slug },
    {
      id: "verdict",
      header: "Verdict",
      cell: ({ row }) => <Badge variant="outline">{verdictLabel(row.original.eval_verdict)}</Badge>,
    },
    {
      id: "score",
      header: "Score",
      accessorFn: (row) => formatScore(row.marketing_engine_score),
      meta: { align: "right" as const },
    },
    {
      id: "revisions",
      header: "Revisions",
      accessorFn: (row) => (row.revision_count === null ? "–" : String(row.revision_count)),
      meta: { align: "right" as const },
    },
    {
      id: "open",
      header: "Detail",
      cell: ({ row }) => (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          data-nonmutating
          onClick={(event) => {
            event.stopPropagation();
            open(row.original);
          }}
        >
          Open
        </Button>
      ),
    },
  ];

  let table: ReactNode;
  if (log.isError) {
    table = (
      <Alert>
        <AlertTitle>Campaign Log unavailable</AlertTitle>
        <AlertDescription>{messageFor(log.error, areaName, "CVCE telemetry")}</AlertDescription>
      </Alert>
    );
  } else {
    const filtered = verdict !== null || format !== null || days !== DEFAULT_DAYS;
    const state = log.data ? campaignLogState(log.data.data, filtered) : null;
    const rows = log.data?.data.campaign_log ?? [];
    table =
      state && log.data && !log.data.data.available ? (
        <Alert>
          <AlertTitle>Campaign Log unavailable</AlertTitle>
          <AlertDescription>{state}</AlertDescription>
        </Alert>
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          pending={log.isPending}
          page={{ pageIndex: 0, pageSize: Math.max(rows.length, 1), rowCount: rows.length }}
          onPageChange={() => {}}
          onRowClick={open}
          emptyMessage={state ?? "No campaigns logged yet."}
          caption={`Marketing assets, last ${days} days, newest first`}
        />
      );
  }

  return (
    <div className="flex flex-col gap-4">
      <FilterBar layout="inline" filters={filters} values={{ ...values, days: String(days) }} onChange={set} />
      {table}
      {log.data && log.data.data.campaign_log.length >= 200 && (
        <p className="text-sm text-ks-ink">Showing the newest 200 assets. Narrow the window or filters to see older ones.</p>
      )}
      <AssetSheet assetId={assetId} onClose={() => set({ ...values, asset: null })} areaName={areaName} />
    </div>
  );
}
